// public/v3/src/components/tier-bump-review-modal.jsx — bulk up-front checklist
// for the CCM tier auto-bump. Lists Standard-CCM (99490) patients who logged
// >= 90 CCM minutes and lets the operator pick ONE tier per patient via a
// mutually-exclusive radio group:
//   ( ) Affirm moderate-to-high MDM            -> 99487 (Complex CCM)
//   ( ) Affirm >= 50% physician personal time  -> 99491 (Practitioner-Led)
//   ( ) Keep 99490                             (neither — Standard CCM)
// The downstream contract is unchanged: onApply receives
// { [patientId]: { mdm, physicianTime } } with AT MOST ONE of the two true
// (the radio can no longer set both; resolveAffirmedTier still tie-breaks MDM
// first for any legacy caller). A neutral "highest reimbursement at this time"
// marker is shown per tier — informational ONLY; the ranking never drives the
// selection. The choice state is SEEDED from each row's existing same-month
// override (`row.existingCode`, read from ccmTierMonth[month]) so a prior
// 99487/99491 affirmation renders checked, and onApply emits an explicit
// { mdm, physicianTime } entry for EVERY displayed row — an untouched row with
// a stale override therefore gets an explicit clear (or re-affirm) instead of
// silently surviving. Pure UI.

import React, { useState, useEffect } from 'react';
import { useUnsavedChangesGuard } from '../lib/hooks/use-unsaved-changes-guard';
import { rankCcmTiersByReimbursement, RATE_LOCALITY_LABEL } from '../lib/billing-generate';

function resultCode(a) {
  if (a && a.mdm) return '99487';
  if (a && a.physicianTime) return '99491';
  return '99490 (keep)';
}

// Which of the three affirmable codes reflects the current affirmation, so the
// radio group renders with the right option checked. Defaults to keeping 99490.
function selectedCode(a) {
  if (a && a.mdm) return '99487';
  if (a && a.physicianTime) return '99491';
  return '99490';
}

// Seed one explicit { mdm, physicianTime } entry per row from the row's
// existing same-month override (row.existingCode: '99487' | '99491' | null).
// Every displayed row gets an entry — so onApply(affirm) always carries an
// explicit decision per row (a both-false entry is an explicit "Keep 99490"
// clear; writeCcmTierAffirmation no-ops when nothing was stored anyway).
function seedAffirmFromRows(rows) {
  const out = {};
  for (const r of rows || []) {
    out[r.patientId] = {
      mdm: r.existingCode === '99487',
      physicianTime: r.existingCode === '99491',
    };
  }
  return out;
}

export function TierBumpReviewModal({ rows, onClose, onApply, busy }) {
  // rows: [{ patientId, name, minutes, physicianPct, existingCode }]
  // (physicianPct is 0-1; existingCode is the prior ccmTierMonth[month] code)
  const [affirm, setAffirm] = useState(() => seedAffirmFromRows(rows));
  // Radio semantics: choosing one tier sets exactly one of { mdm, physicianTime }
  // true (or neither, for "Keep 99490"). This is what guarantees the downstream
  // { mdm, physicianTime } contract can never carry both true from the UI.
  const choose = (pid, code) =>
    setAffirm((prev) => ({
      ...prev,
      [pid]: { mdm: code === '99487', physicianTime: code === '99491' },
    }));

  // Toggle state is unsaved input — route every dismissal path (backdrop tap,
  // Cancel, Esc) through the shared guard so a fat-finger backdrop tap on iPad
  // doesn't silently discard affirmations. Mirrors complex-ccm-review-modal.jsx.
  const { attemptDismiss, showDiscardPrompt, confirmDiscard, keepEditing } =
    useUnsavedChangesGuard({ values: { affirm }, onClose, busy });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') attemptDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attemptDismiss]);

  return (
    <div className="modal-backdrop" onClick={attemptDismiss}>
      <div className="modal" role="dialog" aria-modal="true"
        aria-label="Review CCM tier upgrades" onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720 }}>
        <div className="modal-head"><h3>Higher CCM tier available — {rows.length} patient(s)</h3></div>
        {showDiscardPrompt ? (
          <>
            <div className="modal-body">
              <h3 style={{ marginTop: 0 }}>Discard affirmations?</h3>
              <p>You've checked tier upgrades but haven't applied them yet.</p>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={keepEditing}>Keep editing</button>
              <button className="btn warn" onClick={confirmDiscard}>Discard</button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>
                These patients logged at least 90 CCM minutes this month. Pick one tier per patient
                where it applies — the attestation is added to the billing note. Choose
                “Keep 99490” to bill Standard CCM. The “$ highest at this time” tag is neutral
                reference only — confirm the clinical basis before affirming a higher tier.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rows.map((r) => {
                  const a = affirm[r.patientId] || {};
                  const chosen = selectedCode(a);
                  const lowPhys = (r.physicianPct || 0) < 0.5;
                  // Neutral reimbursement ranking at THIS patient's minutes. Marks
                  // the top-paying affirmable tier — informational only, never
                  // pre-selected (see file header + compliance constraints).
                  const topCode = ((rankCcmTiersByReimbursement(r.minutes) || [])[0] || {}).code;
                  const payTag = (code) =>
                    code === topCode ? (
                      <span title={`Highest allowed reimbursement at ${r.minutes} min · ${RATE_LOCALITY_LABEL}`}
                        style={{ marginLeft: 4, fontSize: 11, color: 'var(--ink-3)',
                          border: '1px solid var(--line)', borderRadius: 6, padding: '0 4px' }}>
                        $ highest here
                      </span>
                    ) : null;
                  return (
                    <div key={r.patientId}
                      style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: 10,
                        alignItems: 'center', padding: '8px 10px', border: '1px solid var(--line)',
                        borderRadius: 'var(--r)' }}>
                      <div>
                        <b>{r.name}</b> · {r.minutes} min
                      </div>
                      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                        <input type="radio" name={`tier-${r.patientId}`} disabled={busy}
                          checked={chosen === '99487'}
                          onChange={() => choose(r.patientId, '99487')} />
                        MDM -&gt; 99487{payTag('99487')}
                      </label>
                      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                        <input type="radio" name={`tier-${r.patientId}`} disabled={busy}
                          checked={chosen === '99491'}
                          onChange={() => choose(r.patientId, '99491')} />
                        &ge;50% physician -&gt; 99491
                        <span style={{ color: lowPhys ? 'var(--warn, #b45309)' : 'var(--ink-3)', fontSize: 12 }}>
                          ({Math.round((r.physicianPct || 0) * 100)}%)
                        </span>
                        {payTag('99491')}
                      </label>
                      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                        <input type="radio" name={`tier-${r.patientId}`} disabled={busy}
                          checked={chosen === '99490'}
                          onChange={() => choose(r.patientId, '99490')} />
                        Keep 99490{payTag('99490')}
                      </label>
                      <span style={{ fontSize: 12, color: 'var(--ink-3)', minWidth: 92, textAlign: 'right' }}>
                        -&gt; {resultCode(a)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={attemptDismiss} disabled={busy}>Cancel</button>
              <button className="btn primary" onClick={() => onApply(affirm)} disabled={busy}>
                {busy ? 'Applying…' : 'Apply & Generate all'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
